/**
 * Route Permission Guard Component
 *
 * A component that checks user permissions for route access based on the current pathname.
 * Uses the centralized route access logic from @/@studio/utils/routeAccess.
 */

'use client';

import { useFeatureFlags } from '@/@feature-flags/hooks/useFeatureFlags';
import { getRouteAccess } from '@/@studio/utils/routeAccess';
import { RoutePermissionGuardProps } from '@/types';
import { useSession } from '@/providers/enhanced-session';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo } from 'react';

/**
 * Route Permission Guard Component
 *
 * Automatically determines the current route and checks permissions based on
 * the centralized route access rules (internal routes, license requirements,
 * freemium restrictions, and feature-flagged routes) in @/@studio/utils/routeAccess.
 *
 * @param props - Component props
 * @returns JSX element with children or null if access denied
 */
function RoutePermissionGuard({ children, redirectToHome = true }: RoutePermissionGuardProps) {
    const pathname = usePathname();
    const router = useRouter();
    const { data: session, status } = useSession();
    const isSessionLoading = status === 'loading';
    const { isLoading: isLoadingFlags, hasFlag } = useFeatureFlags();

    // Check centralized route access rules, including feature-flagged routes
    const routeAccess = useMemo(() => {
        if (!pathname || isSessionLoading) return { canAccess: true, isDisabled: false };

        return getRouteAccess(pathname, {
            user: {
                isInternal: session?.user?.isInternal,
                hasStudioAIAccess: session?.user?.hasStudioAIAccess,
                isUnaffiliated: session?.user?.isUnaffiliated,
            },
            org: session?.org,
            // Omit the flag checker while flags are still loading — `hasFlag`
            // returns false for an unfetched key, which would veto every
            // FEATURE_FLAGGED_ROUTE and bounce the user to /home on a cold load
            // whenever the server flag prefetch is slow or fails. `getRouteAccess`
            // only ever uses `hasFlag` to deny an otherwise-granted route, so an
            // absent checker fails open for all seven until the real values
            // arrive (RouteAccessParams.hasFlag's documented contract).
            hasFlag: isLoadingFlags ? undefined : hasFlag,
        });
    }, [
        pathname,
        isSessionLoading,
        isLoadingFlags,
        session?.user?.isInternal,
        session?.user?.hasStudioAIAccess,
        session?.user?.isUnaffiliated,
        session?.org,
        hasFlag,
    ]);

    // Allow access while the session is still resolving; otherwise defer to
    // routeAccess, which already fails open on the feature-flag veto while
    // flags load (see the `hasFlag` note above).
    const canAccess = isSessionLoading || routeAccess.canAccess;
    const redirectTo = routeAccess.redirectTo;

    // Redirect if access is denied (only after session has resolved)
    useEffect(() => {
        if (!isSessionLoading && redirectToHome && !canAccess && redirectTo) {
            router.replace(redirectTo);
        }
    }, [isSessionLoading, canAccess, redirectTo, redirectToHome, router]);

    // Don't render if access is denied (session must be resolved)
    if (!canAccess) {
        return null;
    }

    // Render children if access is granted (or session still loading)
    return <>{children}</>;
}

export default RoutePermissionGuard;
